import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that displays a popup notification under the cart icon
 * when a product is added to the cart.
 */
class CartPopupNotification extends Component {
  /** @type {number | undefined} */
  #hideTimeout;
  /** @type {number} */
  #displayDuration = 3000; // 3 seconds

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    if (this.#hideTimeout) clearTimeout(this.#hideTimeout);
  }

  /**
   * Handles the cart update event
   * @param {CustomEvent} event
   */
  #onCartUpdate = (event) => {
    const { data, resource } = event.detail || {};
    
    // Only show popup when coming from product form (add to cart action)
    if (data?.source !== 'product-form-component') return;
    
    // Don't show if there was an error
    if (data?.didError) return;

    // resource is the Shopify cart add response — contains product_title, vendor, image, price, etc.
    if (resource && (resource.title || resource.product_title || resource.product_id)) {
      this.#showNotificationFast(resource);
    }
  };

  /**
   * Fetches cart in background without blocking notification display
   */
  #fetchCartInBackground() {
    fetch('/cart.js', {
      method: 'GET',
      headers: { 'Accept': 'application/json' }
    }).catch(() => {
      // Silent fail — notification already shown
    });
  };

  /**
   * Shows notification immediately with variant data (no async fetches)
   * @param {Object} item - Cart item or variant data
   */
  #showNotificationFast(item) {
    if (!item) return;

    const productDataObj = {
      vendor: item.vendor || item.product_vendor || '',
      title: item.product_title || item.title || 'Product',
      image: item.image || item.featured_image?.url || '',
      price: this.#formatMoney(item.price || 0),
      quantity: item.quantity || 1,
      variant: item.variant_title || '',
      url: item.url || ''
    };

    this.#updatePopupContent(productDataObj);
    this.#positionPopup();
    this.classList.add('cart-popup-notification--visible');

    if (this.#hideTimeout) clearTimeout(this.#hideTimeout);
    this.#hideTimeout = setTimeout(() => {
      this.#hidePopup();
    }, this.#displayDuration);
  }

  /**
   * Positions the popup aligned with the cart icon
   */
  #positionPopup() {
    const cartIcon = document.querySelector('.header-actions__cart-icon, cart-icon');
    if (!cartIcon) return;

    const cartIconRect = cartIcon.getBoundingClientRect();
    
    // Calculate the right offset so popup's right edge aligns with cart icon's right edge
    const rightOffset = window.innerWidth - cartIconRect.right;
    
    // Position the popup right below the cart icon
    const topOffset = cartIconRect.bottom + 8; // 8px gap below the cart icon
    
    this.style.right = `${rightOffset}px`;
    this.style.top = `${topOffset}px`;
  }

  /**
   * Formats price in cents to currency string
   * @param {number} cents
   * @returns {string}
   */
  #formatMoney(cents) {
    if (!cents) return '';
    const amount = Math.round(cents / 100);
    return `$${amount}`;
  }

  /**
   * Updates the popup content with product data
   * @param {Object} productData
   */
  #updatePopupContent(productData) {
    const { vendor, title, image, price, originalPrice, isOnSale } = productData;

    // Build price HTML - show original price struck through and sale price in red for sale items
    // let priceHtml = '';
    // if (price) {
    //   if (isOnSale && originalPrice) {
    //     priceHtml = `
    //       <p class="cart-popup-notification__price">
    //         <span class="cart-popup-notification__price--original">${originalPrice}</span>
    //         <span class="cart-popup-notification__price--sale">${price}</span>
    //       </p>
    //     `;
    //   } else {
    //     priceHtml = `<p class="cart-popup-notification__price">${price}</p>`;
    //   }
    // }

    // Build the popup HTML
    this.innerHTML = `
      <div class="cart-popup-notification__content">
        <div class="cart-popup-notification__body">
          ${image ? `<img class="cart-popup-notification__image" src="${image}" alt="${title}" loading="lazy" />` : ''}
          <div class="cart-popup-notification__details">
            <span class="cart-popup-notification__added-text">Added to bag</span>
            ${vendor ? `<p class="cart-popup-notification__vendor">${vendor}</p>` : ''}
            <p class="cart-popup-notification__product-title">${title}</p>
          </div>
        </div>
        <div class="cart-popup-notification__footer">
          <button type="button" class="cart-popup-notification__cta" onclick="window.location.href='/cart'">Go to bag</button>
        </div>
      </div>
    `;
  }

  /**
   * Hides the popup
   */
  #hidePopup() {
    this.classList.remove('cart-popup-notification--visible');
    if (this.#hideTimeout) {
      clearTimeout(this.#hideTimeout);
      this.#hideTimeout = undefined;
    }
  }
}

if (!customElements.get('cart-popup-notification')) {
  customElements.define('cart-popup-notification', CartPopupNotification);
}

export { CartPopupNotification };